import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Controller, FieldError as RHFFieldError, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useToast } from '../../hooks/useToast';
import manifestService, {
  Manifest,
  ManifestApiError,
} from '../../services/manifestService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';

export interface CreateManifestProps {
  onCancel: () => void;
  onSuccess: (manifest: Manifest) => void;
}

interface DropdownOption {
  value: string;
  label: string;
  code?: string;
}

const RUN_NO_PATTERN = /^[A-Za-z]+-\d+$/;

const createManifestBaseSchema = z.object({
    date: z.string().min(1, 'Date is required'),
    time: z.string().min(1, 'Time is required'),
    vendor: z.string().min(1, 'Vendor is required'),
    originHub: z.string().min(1, 'Origin Hub is required'),
    manifestType: z.enum(['EXPORT', 'IMPORT']),
    noOfBags: z.coerce
      .number({ invalid_type_error: 'Number of bags must be a number' })
      .int('Number of bags must be a whole number')
      .min(1, 'Number of bags must be at least 1'),
    forwarder: z.string().optional(),
    service: z.string().optional(),
    masterNo: z.string().optional(),
    masterEdiBagNo: z.string().optional(),
    bagNoRange: z.string().optional(),
    remark: z.string().optional(),
    runNo: z
      .string()
      .optional()
      .refine(
        value => !value || RUN_NO_PATTERN.test(value),
        'Run number must match ALPHABET-NUMBER format (e.g. A-123)'
      ),
    flightNo: z.string().optional(),
    vendorCdNo: z.string().optional(),
    arrivalDate: z.string().optional(),
    arrivalTime: z.string().optional(),
    company: z.string().optional(),
    license: z.string().optional(),
    destinationHub: z.string().optional(),
});

const createManifestSchema = createManifestBaseSchema.superRefine(
  (data, ctx) => {
    if (!!data.arrivalDate !== !!data.arrivalTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Arrival date and time must be provided together',
        path: [data.arrivalDate ? 'arrivalTime' : 'arrivalDate'],
      });
    }
  }
);

type CreateManifestFormValues = z.infer<typeof createManifestSchema>;

const NONE_VALUE = '__none__';

function FieldErrorMessage({ error }: { error?: RHFFieldError }) {
  if (!error?.message) return null;
  return <p className='text-xs text-destructive mt-1'>{error.message}</p>;
}

function RequiredMark() {
  return <span className='text-destructive ml-1'>*</span>;
}

const CreateManifest: React.FC<CreateManifestProps> = ({
  onCancel,
  onSuccess,
}) => {
  const [loadingMasterData, setLoadingMasterData] = useState(true);
  const [vendors, setVendors] = useState<DropdownOption[]>([]);
  const [hubs, setHubs] = useState<DropdownOption[]>([]);
  const [companies, setCompanies] = useState<DropdownOption[]>([]);
  const [services, setServices] = useState<DropdownOption[]>([]);
  const [licenses, setLicenses] = useState<DropdownOption[]>([]);
  const { toast } = useToast();

  const form = useForm<CreateManifestFormValues>({
    resolver: zodResolver(createManifestSchema),
    defaultValues: {
      date: format(new Date(), 'yyyy-MM-dd'),
      time: format(new Date(), 'HH:mm'),
      vendor: '',
      originHub: '',
      manifestType: 'EXPORT',
      noOfBags: 1,
    },
  });

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = form;

  // Fetch master data
  useEffect(() => {
    const fetchMasterData = async () => {
      setLoadingMasterData(true);
      try {
        const API_BASE_URL =
          import.meta.env.VITE_API_URL || 'https://awbbackend.bliitz.in';
        const token = localStorage.getItem('accessToken');
        const headers = { Authorization: `Bearer ${token}` };

        const [vendorsRes, hubsRes, companiesRes, servicesRes, licensesRes] =
          await Promise.all([
            fetch(`${API_BASE_URL}/api/master-data/vendor/dropdown`, {
              headers,
            }).then(r => r.json()),
            fetch(`${API_BASE_URL}/api/master-data/hub/dropdown`, {
              headers,
            }).then(r => r.json()),
            fetch(`${API_BASE_URL}/api/master-data/company/dropdown`, {
              headers,
            }).then(r => r.json()),
            fetch(`${API_BASE_URL}/api/master-data/service/dropdown`, {
              headers,
            }).then(r => r.json()),
            fetch(`${API_BASE_URL}/api/master-data/license/dropdown`, {
              headers,
            }).then(r => r.json()),
          ]);

        setVendors(vendorsRes.data || []);
        setHubs(hubsRes.data || []);
        setCompanies(companiesRes.data || []);
        setServices(servicesRes.data || []);
        setLicenses(licensesRes.data || []);
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to load dropdown data. Please refresh the page.',
          variant: 'destructive',
        });
      } finally {
        setLoadingMasterData(false);
      }
    };

    fetchMasterData();
  }, [toast]);

  const applyServerFieldErrors = (details: Record<string, string>): boolean => {
    const formFields = Object.keys(createManifestBaseSchema.shape);
    let mapped = false;

    for (const [field, message] of Object.entries(details)) {
      if (formFields.includes(field)) {
        setError(field as keyof CreateManifestFormValues, {
          type: 'server',
          message,
        });
        mapped = true;
      }
    }

    return mapped;
  };

  const onSubmit = async (values: CreateManifestFormValues) => {
    try {
      const manifest = await manifestService.createManifest(values);
      toast({
        title: 'Success',
        description: `Manifest ${manifest.manifestNo} created successfully`,
      });
      onSuccess(manifest);
    } catch (error: unknown) {
      if (
        error instanceof ManifestApiError &&
        error.code === 'VALIDATION_ERROR' &&
        error.details &&
        applyServerFieldErrors(error.details)
      ) {
        return;
      }

      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to create manifest',
        variant: 'destructive',
      });
    }
  };

  const errorBorder = (hasError: boolean) =>
    hasError ? 'border-destructive' : undefined;

  return (
    <div className='space-y-4 sm:space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-2 sm:gap-4'>
          <Button
            variant='outline'
            onClick={onCancel}
            size='sm'
            className='sm:size-default'
          >
            <ArrowLeft className='h-4 w-4 sm:mr-2' />
            <span className='hidden sm:inline'>BACK</span>
          </Button>
          <h1 className='text-lg sm:text-2xl font-bold'>NEW MANIFEST</h1>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          className='bg-green-600 hover:bg-green-700 w-full sm:w-auto'
        >
          {isSubmitting ? (
            <Loader2 className='h-4 w-4 animate-spin mr-2' />
          ) : (
            <Plus className='h-4 w-4 mr-2' />
          )}
          CREATE MANIFEST
        </Button>
      </div>

      {/* Form */}
      <div className='bg-background border rounded-lg p-4 sm:p-6'>
        <h2 className='text-lg sm:text-xl font-semibold mb-4 sm:mb-6'>
          Manifest Details
        </h2>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-x-8 lg:gap-y-4'>
          {/* Left Column */}
          <div className='space-y-4'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div>
                <Label htmlFor='date'>
                  DATE
                  <RequiredMark />
                </Label>
                <Input
                  id='date'
                  type='date'
                  className={errorBorder(!!errors.date)}
                  {...register('date')}
                />
                <FieldErrorMessage error={errors.date} />
              </div>
              <div>
                <Label htmlFor='time'>
                  TIME
                  <RequiredMark />
                </Label>
                <Input
                  id='time'
                  type='time'
                  className={errorBorder(!!errors.time)}
                  {...register('time')}
                />
                <FieldErrorMessage error={errors.time} />
              </div>
            </div>

            <div>
              <Label>
                VENDOR
                <RequiredMark />
              </Label>
              <Controller
                name='vendor'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={loadingMasterData}
                  >
                    <SelectTrigger className={errorBorder(!!errors.vendor)}>
                      <SelectValue
                        placeholder={
                          loadingMasterData ? 'Loading...' : 'Select vendor...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.length === 0 ? (
                        <SelectItem value='__empty__' disabled>
                          No vendors available
                        </SelectItem>
                      ) : (
                        vendors.map(v => (
                          <SelectItem key={v.value} value={v.value}>
                            {v.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              <FieldErrorMessage error={errors.vendor} />
            </div>

            <div>
              <Label>FORWARDER</Label>
              <Controller
                name='forwarder'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value || NONE_VALUE}
                    onValueChange={value =>
                      field.onChange(value === NONE_VALUE ? '' : value)
                    }
                    disabled={loadingMasterData}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          loadingMasterData
                            ? 'Loading...'
                            : 'Select forwarder...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>-- None --</SelectItem>
                      {companies.map(c => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <FieldErrorMessage error={errors.forwarder} />
            </div>

            <div>
              <Label>SERVICE</Label>
              <Controller
                name='service'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value || NONE_VALUE}
                    onValueChange={value =>
                      field.onChange(value === NONE_VALUE ? '' : value)
                    }
                    disabled={loadingMasterData}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          loadingMasterData ? 'Loading...' : 'Select service...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>-- None --</SelectItem>
                      {services.map(s => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <FieldErrorMessage error={errors.service} />
            </div>

            <div>
              <Label htmlFor='masterNo'>MASTER NO.</Label>
              <Input
                id='masterNo'
                placeholder='Enter master number...'
                {...register('masterNo')}
              />
              <FieldErrorMessage error={errors.masterNo} />
            </div>

            <div>
              <Label htmlFor='masterEdiBagNo'>MASTER EDI BAG NO</Label>
              <Input
                id='masterEdiBagNo'
                placeholder='Enter master EDI bag number...'
                {...register('masterEdiBagNo')}
              />
              <FieldErrorMessage error={errors.masterEdiBagNo} />
            </div>

            <div>
              <Label htmlFor='bagNoRange'>BAG NO. RANGE</Label>
              <Input
                id='bagNoRange'
                placeholder='Enter bag number range...'
                {...register('bagNoRange')}
              />
              <FieldErrorMessage error={errors.bagNoRange} />
            </div>

            <div>
              <Label htmlFor='remark'>REMARK</Label>
              <Textarea
                id='remark'
                placeholder='Enter remarks...'
                rows={3}
                {...register('remark')}
              />
              <FieldErrorMessage error={errors.remark} />
            </div>
          </div>

          {/* Right Column */}
          <div className='space-y-4'>
            <div>
              <Label>
                ORIGIN HUB
                <RequiredMark />
              </Label>
              <Controller
                name='originHub'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={loadingMasterData}
                  >
                    <SelectTrigger className={errorBorder(!!errors.originHub)}>
                      <SelectValue
                        placeholder={
                          loadingMasterData
                            ? 'Loading...'
                            : 'Select origin hub...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {hubs.length === 0 ? (
                        <SelectItem value='__empty__' disabled>
                          No hubs available
                        </SelectItem>
                      ) : (
                        hubs.map(h => (
                          <SelectItem key={h.value} value={h.value}>
                            {h.code ? `${h.code} - ${h.label}` : h.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              <FieldErrorMessage error={errors.originHub} />
            </div>

            <div>
              <Label>DESTINATION HUB</Label>
              <Controller
                name='destinationHub'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value || NONE_VALUE}
                    onValueChange={value =>
                      field.onChange(value === NONE_VALUE ? '' : value)
                    }
                    disabled={loadingMasterData}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          loadingMasterData
                            ? 'Loading...'
                            : 'Select destination hub...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>-- None --</SelectItem>
                      {hubs.map(h => (
                        <SelectItem key={h.value} value={h.value}>
                          {h.code ? `${h.code} - ${h.label}` : h.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div>
              <Label htmlFor='runNo'>RUN NUMBER</Label>
              <Input
                id='runNo'
                placeholder='ALPHABET-NUMBER'
                className={errorBorder(!!errors.runNo)}
                {...register('runNo')}
              />
              <FieldErrorMessage error={errors.runNo} />
            </div>

            <div>
              <Label htmlFor='flightNo'>FLIGHT NO</Label>
              <Input
                id='flightNo'
                placeholder='Enter flight number...'
                {...register('flightNo')}
              />
              <FieldErrorMessage error={errors.flightNo} />
            </div>

            <div>
              <Label htmlFor='vendorCdNo'>VENDOR CD NO.</Label>
              <Input
                id='vendorCdNo'
                placeholder='Enter vendor CD number...'
                {...register('vendorCdNo')}
              />
              <FieldErrorMessage error={errors.vendorCdNo} />
            </div>

            <div>
              <Label htmlFor='noOfBags'>NO. OF BAGS</Label>
              <Input
                id='noOfBags'
                type='number'
                min={1}
                className={errorBorder(!!errors.noOfBags)}
                {...register('noOfBags')}
              />
              <FieldErrorMessage error={errors.noOfBags} />
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div>
                <Label htmlFor='arrivalDate'>ARRIVAL DATE</Label>
                <Input
                  id='arrivalDate'
                  type='date'
                  className={errorBorder(!!errors.arrivalDate)}
                  {...register('arrivalDate')}
                />
                <FieldErrorMessage error={errors.arrivalDate} />
              </div>
              <div>
                <Label htmlFor='arrivalTime'>ARRIVAL TIME</Label>
                <Input
                  id='arrivalTime'
                  type='time'
                  className={errorBorder(!!errors.arrivalTime)}
                  {...register('arrivalTime')}
                />
                <FieldErrorMessage error={errors.arrivalTime} />
              </div>
            </div>

            <div>
              <Label>COMPANY</Label>
              <Controller
                name='company'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value || NONE_VALUE}
                    onValueChange={value =>
                      field.onChange(value === NONE_VALUE ? '' : value)
                    }
                    disabled={loadingMasterData}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          loadingMasterData ? 'Loading...' : 'Select company...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>-- None --</SelectItem>
                      {companies.map(c => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <FieldErrorMessage error={errors.company} />
            </div>

            <div>
              <Label>LICENSE</Label>
              <Controller
                name='license'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value || NONE_VALUE}
                    onValueChange={value =>
                      field.onChange(value === NONE_VALUE ? '' : value)
                    }
                    disabled={loadingMasterData}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          loadingMasterData ? 'Loading...' : 'Select license...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>-- None --</SelectItem>
                      {licenses.map(l => (
                        <SelectItem key={l.value} value={l.value}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <FieldErrorMessage error={errors.license} />
            </div>

            <div>
              <Label>
                MANIFEST TYPE
                <RequiredMark />
              </Label>
              <Controller
                name='manifestType'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='EXPORT'>EXPORT</SelectItem>
                      <SelectItem value='IMPORT'>IMPORT</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <FieldErrorMessage error={errors.manifestType} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateManifest;
